import { useState, useEffect, useMemo } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import BaseModal from "@components/modals/BaseModal";
import Icon1 from "@assets/icons/Icon";
import CheckBox from "@components/ui/selection/CheckBox";

const NEED_CARDS = [
  { id: "entretien",          label: "Entretien\nRévision" },
  { id: "reparation",         label: "Réparation\nPanne" },
  { id: "diagnostic",         label: "Diagnostic" },
  { id: "controle_technique", label: "Contrôle\ntechnique" },
  { id: "pneumatique",        label: "Pneus" },
  { id: "carrosserie",        label: "Carrosserie\nVitrage" },
];

function ServiceIcon({ type, size = 24, color = "currentColor" }: { type: string; size?: number; color?: string }) {
  const s: React.CSSProperties = { width: size, height: size, flexShrink: 0 };
  const p = { stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  if (type === "entretien") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>;
  if (type === "reparation") return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
  if (type === "diagnostic") return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
  if (type === "controle_technique") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>;
  if (type === "pneumatique") return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="22" /><line x1="2" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="22" y2="12" /></svg>;
  if (type === "carrosserie") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M6 10l2.5-5h7L18 10" /><rect x="2" y="10" width="20" height="7" rx="2" /><circle cx="7" cy="17" r="2" fill={color} stroke="none" /><circle cx="17" cy="17" r="2" fill={color} stroke="none" /><line x1="12" y1="5" x2="12" y2="10" /></svg>;
  return <span style={s} />;
}

const TYPE_LABELS: Record<string, string> = {
  entretien: "Entretien / Révision",
  reparation: "Réparation / Panne",
  diagnostic: "Diagnostic",
  controle_technique: "Contrôle technique",
  pneumatique: "Pneus",
  carrosserie: "Carrosserie / Vitrage",
};

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  entretien: { bg: "#DBEAFE", color: "#1D4ED8" },
  reparation: { bg: "#FEE2E2", color: "#DC2626" },
  diagnostic: { bg: "#E0E7FF", color: "#4338CA" },
  controle_technique: { bg: "#D1FAE5", color: "#065F46" },
  pneumatique: { bg: "#FEF3C7", color: "#B45309" },
  carrosserie: { bg: "#FCE7F3", color: "#BE185D" },
};

const DURATION_OPTIONS = [
  { value: "15", label: "15 min" },
  { value: "20", label: "20 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "1h" },
  { value: "90", label: "1h30" },
  { value: "120", label: "2h" },
  { value: "150", label: "2h30" },
  { value: "180", label: "3h" },
  { value: "240", label: "4h" },
  { value: "300", label: "5h" },
  { value: "360", label: "6h" },
  { value: "480", label: "8h" },
];

const MARGIN_OPTIONS = [
  { value: "0", label: "Aucune marge" },
  { value: "1", label: "1 jour" },
  { value: "2", label: "2 jours" },
  { value: "3", label: "3 jours" },
  { value: "5", label: "5 jours" },
  { value: "7", label: "1 semaine" },
  { value: "14", label: "2 semaines" },
];

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${m.toString().padStart(2, "0")}` : `${h}h`;
}

// ─── Edit / Create Modal ─────────────────────────────────────

function InterventionModal({
  intervention,
  onClose,
  onSave,
  onDelete,
}: {
  intervention: Intervention | null; // null = create mode
  onClose: () => void;
  onSave: (data: { name: string; estimated_minutes: number; min_days_margin: number; type: string; active: boolean; fast: boolean }) => void;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(intervention?.name || "");
  const [minutes, setMinutes] = useState(String(intervention?.estimated_minutes || 60));
  const [margin, setMargin] = useState(String(intervention?.min_days_margin || 0));
  const [type, setType] = useState(intervention?.type || "entretien");
  const [active, setActive] = useState(intervention?.active ?? true);
  const [fast, setFast] = useState(intervention?.fast ?? false);

  const isCreate = !intervention;

  return (
    <BaseModal
      onClose={onClose}
      title={isCreate ? "Nouvelle intervention" : "Modifier l'intervention"}
      startIcon={<Icon1 name="sett-interventions" size={20} />}
      width="max-w-lg"
    >
      <div className="flex flex-col gap-4 p-4">
        <TextField
          label="Nom de l'intervention"
          value={name}
          onChange={(v: string) => setName(v)}
          placeholder="ex: Vidange + filtre huile"
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-h4 text-neutral-400">Type</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
            {NEED_CARDS.map((card) => {
              const isSelected = type === card.id;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setType(card.id)}
                  style={{
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    gap: 5, padding: "12px 6px", borderRadius: 12, cursor: "pointer",
                    border: isSelected ? "2px solid #5C6BFF" : "1.5px solid #E5E5E5",
                    background: isSelected ? "#F0F1FF" : "#FAFAFA",
                    transition: "all 0.15s",
                  }}
                >
                  <ServiceIcon type={card.id} size={22} color={isSelected ? "#5C6BFF" : "#555"} />
                  <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: isSelected ? "#5C6BFF" : "#333", textAlign: "center", whiteSpace: "pre-line", lineHeight: "14px" }}>{card.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <DropDown
          label="Durée estimée"
          value={minutes}
          options={DURATION_OPTIONS}
          onSelect={(v) => setMinutes(String(v))}
        />

        <DropDown
          label="Marge minimum pour restitution"
          value={margin}
          options={MARGIN_OPTIONS}
          onSelect={(v) => setMargin(String(v))}
        />

        <div className="text-h4 text-neutral-400">
          {parseInt(margin) > 0
            ? `Le client ne pourra pas choisir une date de restitution avant ${margin} jour${parseInt(margin) > 1 ? "s" : ""} après le dépôt pour cette intervention.`
            : "Aucune marge supplémentaire appliquée à la date de restitution."}
        </div>

        <div className="flex flex-row items-center gap-2">
          <CheckBox checked={fast} onChange={() => setFast(!fast)} />
          <span className="text-h3 text-neutral-500">Service Rapide (délai en heures, disponibilité mécanicien SR)</span>
        </div>

        {!isCreate && (
          <div className="flex flex-row items-center gap-2">
            <CheckBox checked={active} onChange={() => setActive(!active)} />
            <span className="text-h3 text-neutral-500">Active (visible dans le widget)</span>
          </div>
        )}

        <div className="flex flex-row items-center gap-2 pt-2">
          <Button
            label={isCreate ? "Créer" : "Enregistrer"}
            onClick={() => onSave({
              name: name.trim(),
              estimated_minutes: parseInt(minutes),
              min_days_margin: parseInt(margin),
              type,
              active,
              fast,
            })}
            disabled={!name.trim()}
            variant="small"
          />
          {!isCreate && onDelete && (
            <SecondaryButton
              label="Supprimer"
              onClick={onDelete}
              variant="small"
              danger
            />
          )}
        </div>
      </div>
    </BaseModal>
  );
}

// ─── Main Page ───────────────────────────────────────────────

export const Interventions = () => {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const canEdit = isOwnerOrAdmin(userData);

  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [editTarget, setEditTarget] = useState<Intervention | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const fetchInterventions = async () => {
    if (!orgData?.id) return;
    try {
      const data = await interventionsApi.list(orgData.id);
      setInterventions(data);
    } catch {
      console.error("Failed to fetch interventions");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInterventions();
  }, [orgData?.id]);

  const filtered = useMemo(() => {
    let list = interventions;
    if (filterType !== "all") {
      list = list.filter((i) => i.type === filterType);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((i) => i.name.toLowerCase().includes(q));
    }
    return list;
  }, [interventions, search, filterType]);

  // Group filtered interventions by type
  const grouped = useMemo(() => {
    const groups: Record<string, Intervention[]> = {};
    for (const i of filtered) {
      const t = i.type || "entretien";
      if (!groups[t]) groups[t] = [];
      groups[t].push(i);
    }
    return groups;
  }, [filtered]);

  const handleCreate = async (data: { name: string; estimated_minutes: number; min_days_margin: number; type: string; active: boolean; fast: boolean }) => {
    if (!orgData?.id) return;
    try {
      const created = await interventionsApi.create({ ...data, org_id: orgData.id });
      setInterventions((prev) => [...prev, created]);
      setShowCreate(false);
      showNotification({ message: "Intervention créée", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la création", type: "error" });
    }
  };

  const handleUpdate = async (data: { name: string; estimated_minutes: number; min_days_margin: number; type: string; active: boolean; fast: boolean }) => {
    if (!editTarget) return;
    try {
      const updated = await interventionsApi.update(editTarget.id, data);
      setInterventions((prev) => prev.map((i) => (i.id === editTarget.id ? updated : i)));
      setEditTarget(null);
      showNotification({ message: "Intervention mise à jour", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la mise à jour", type: "error" });
    }
  };

  const handleDelete = async () => {
    if (!editTarget) return;
    try {
      await interventionsApi.delete(editTarget.id);
      setInterventions((prev) => prev.filter((i) => i.id !== editTarget.id));
      setEditTarget(null);
      showNotification({ message: "Intervention supprimée", type: "success" });
    } catch {
      showNotification({ message: "Erreur lors de la suppression", type: "error" });
    }
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10 w-full max-w-700">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col">
          <Text1>Interventions</Text1>
          <Text2>Gérez les différentes interventions proposées.</Text2>
          <Text3>
            Configurez la liste des interventions par type. La durée estimée et la marge
            de restitution seront utilisées dans le widget de réservation.
          </Text3>
        </div>

        <div className="flex flex-col bg-white rounded-lg border-1 overflow-hidden">
          {/* Header */}
          <div className="flex flex-row items-center justify-between px-4 pt-4 pb-2">
            <span className="text-h3 text-neutral-400">
              Interventions ({interventions.length})
            </span>
          </div>

          {/* Type filter cards */}
          <div className="px-4 pb-3">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 6 }}>
              <button
                onClick={() => setFilterType("all")}
                style={{
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  gap: 4, padding: "8px 4px", borderRadius: 10, cursor: "pointer",
                  border: filterType === "all" ? "2px solid #5C6BFF" : "1.5px solid #E5E5E5",
                  background: filterType === "all" ? "#F0F1FF" : "#FAFAFA",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 11, color: filterType === "all" ? "#5C6BFF" : "#333", textAlign: "center", lineHeight: "14px" }}>Tous</span>
                <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 10, color: filterType === "all" ? "#5C6BFF" : "#A1A1A1" }}>{interventions.length}</span>
              </button>
              {NEED_CARDS.map((card) => {
                const isSelected = filterType === card.id;
                const count = interventions.filter((i) => i.type === card.id).length;
                return (
                  <button
                    key={card.id}
                    onClick={() => setFilterType(card.id)}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      gap: 4, padding: "8px 4px", borderRadius: 10, cursor: "pointer",
                      border: isSelected ? "2px solid #5C6BFF" : "1.5px solid #E5E5E5",
                      background: isSelected ? "#F0F1FF" : "#FAFAFA",
                      transition: "all 0.15s",
                    }}
                  >
                    <ServiceIcon type={card.id} size={18} color={isSelected ? "#5C6BFF" : "#555"} />
                    <span style={{ fontFamily: "Poppins", fontWeight: 500, fontSize: 10, color: isSelected ? "#5C6BFF" : "#333", textAlign: "center", whiteSpace: "pre-line", lineHeight: "12px" }}>{card.label}</span>
                    {count > 0 && <span style={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 10, color: isSelected ? "#5C6BFF" : "#A1A1A1" }}>{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Search + Create */}
          <div className="flex flex-row items-center gap-2 px-4 pb-3">
            <div className="flex-1">
              <TextField
                value={search}
                onChange={(v: string) => setSearch(v)}
                placeholder="Chercher .."
                noborder
              />
            </div>
            {canEdit && (
              <SecondaryButton
                label="Créer"
                variant="small"
                startIcon={<Icon1 name="plus" size={12} />}
                onClick={() => setShowCreate(true)}
              />
            )}
          </div>

          {/* List grouped by type */}
          {loading ? (
            <div className="px-4 py-8 text-center text-h3 text-neutral-300">
              Chargement...
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-h3 text-neutral-300">
              {search ? "Aucune intervention trouvée" : "Aucune intervention configurée"}
            </div>
          ) : (
            <div className="flex flex-col">
              {Object.entries(grouped).map(([type, items]) => {
                const typeColor = TYPE_COLORS[type] || TYPE_COLORS.entretien;
                return (
                  <div key={type}>
                    {/* Type group header (only show if not filtering by a single type) */}
                    {filterType === "all" && (
                      <div
                        className="flex flex-row items-center gap-2 px-4 py-2 border-t-1"
                        style={{ background: "#FAFAFA" }}
                      >
                        <span
                          className="text-h4 font-semibold px-2 py-0.5 rounded"
                          style={{ background: typeColor.bg, color: typeColor.color }}
                        >
                          {TYPE_LABELS[type] || type}
                        </span>
                        <span className="text-h4 text-neutral-400">
                          {items.length} intervention{items.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                    {items.map((intervention) => (
                      <div
                        key={intervention.id}
                        className="flex flex-row items-center px-4 py-3 border-t-1 hover:bg-neutral-50 transition-colors"
                        style={{ gap: 12 }}
                      >
                        <span
                          className="text-h3 flex-1"
                          style={{
                            color: intervention.active ? "#525252" : "#A1A1A1",
                            fontFamily: "Poppins",
                            textDecoration: intervention.active ? "none" : "line-through",
                          }}
                        >
                          {intervention.name}
                        </span>
                        <span
                          className="text-h5 font-medium px-2 py-0.5 rounded"
                          style={{ background: (TYPE_COLORS[intervention.type] || typeColor).bg, color: (TYPE_COLORS[intervention.type] || typeColor).color, whiteSpace: "nowrap" }}
                        >
                          {TYPE_LABELS[intervention.type] || intervention.type}
                        </span>
                        {intervention.fast && (
                          <span
                            className="text-h5 font-semibold px-2 py-0.5 rounded"
                            style={{ background: "#DBEAFE", color: "#1D4ED8", whiteSpace: "nowrap" }}
                          >
                            SR
                          </span>
                        )}
                        {intervention.min_days_margin > 0 && (
                          <span
                            className="text-h4 font-medium px-2 py-0.5 rounded"
                            style={{ background: "#FEF3C7", color: "#B45309", whiteSpace: "nowrap" }}
                          >
                            +{intervention.min_days_margin}j
                          </span>
                        )}
                        <span
                          className="text-h3 text-neutral-400"
                          style={{ whiteSpace: "nowrap", minWidth: 60, textAlign: "right" }}
                        >
                          {formatDuration(intervention.estimated_minutes)}
                        </span>
                        {canEdit && (
                          <SecondaryButton
                            label="Gérer"
                            variant="small"
                            onClick={() => setEditTarget(intervention)}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreate && (
        <InterventionModal
          intervention={null}
          onClose={() => setShowCreate(false)}
          onSave={handleCreate}
        />
      )}
      {editTarget && (
        <InterventionModal
          intervention={editTarget}
          onClose={() => setEditTarget(null)}
          onSave={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};
